import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  TextField,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Fade,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
} from "@mui/material";
import {
  CheckCircle,
  XCircle,
  QrCode,
  Clock,
  Play,
  Square,
  Hash,
  RotateCcw,
  Share,
  RefreshCw,
  Anchor,
  Tag,
  X,
  Printer,
  ClipboardCopy,
  Camera,
  Keyboard,
} from "lucide-react";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import QRCode from "qrcode.react";
import { makeStyles } from "@material-ui/core/styles";

const STATUS_IDLE = "idle";
const STATUS_LOADING = "loading";
const STATUS_SUCCESS = "success";
const STATUS_ERROR = "error";

const SESSION_STORAGE_KEY = "taplr_scanner_session";
const SESSION_VALUES_KEY = "taplr_scanner_values";

function getOrCreateSession() {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.id && parsed.startedAt) return parsed;
    }
  } catch { /* ignore corrupt data */ }
  return createNewSession();
}

function createNewSession() {
  const session = {
    id: crypto.randomUUID(),
    startedAt: new Date().toISOString(),
  };
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  localStorage.removeItem(SESSION_VALUES_KEY);
  return session;
}

function loadSessionValues() {
  try {
    const stored = localStorage.getItem(SESSION_VALUES_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return { anchor: null, stickies: [] };
}

function saveSessionValues(values) {
  localStorage.setItem(SESSION_VALUES_KEY, JSON.stringify(values));
}

function injectSessionId(url, sessionId) {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set("sessionId", sessionId);
    return urlObj.toString();
  } catch {
    return url;
  }
}

function QrScannerPage() {
  const classes = useStyles();
  const [scanBuffer, setScanBuffer] = useState("");
  const [scanHistory, setScanHistory] = useState([]);
  const [status, setStatus] = useState(STATUS_IDLE);
  const [latestResult, setLatestResult] = useState(null);
  const [session, setSession] = useState(getOrCreateSession);
  const [sessionValues, setSessionValues] = useState(loadSessionValues);
  const [cameraMode, setCameraMode] = useState(false);
  const inputRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const apiBaseUrlRef = useRef(null);

  const updateSessionValues = useCallback((newValues) => {
    setSessionValues(newValues);
    saveSessionValues(newValues);
  }, []);

  // Fetch API base URL and restore active session values from backend on load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("appSettings.json");
        if (!res.ok) return;
        const settings = await res.json();
        apiBaseUrlRef.current = settings.TAPLR_API_BASE_URL;

        // Fetch active session values from the server
        const url = `${settings.TAPLR_API_BASE_URL}/ExternalApiMeasurementLogic/GetActiveSessionValues?sessionId=${encodeURIComponent(session.id)}`;
        const resp = await fetch(url);
        if (!resp.ok || cancelled) return;
        const sessions = await resp.json();

        // BehaviorTypeId: 1 = Anchor, 2 = Sticky
        const anchor = sessions.find((s) => s.behaviorTypeId === 1);
        const stickies = sessions.filter((s) => s.behaviorTypeId === 2);

        const restored = {
          anchor: anchor ? { value: anchor.enumOptionValue, apiName: anchor.apiName, scannedAt: anchor.activeFrom, activeTo: anchor.activeTo } : null,
          stickies: stickies.map((s) => ({ value: s.enumOptionValue, apiName: s.apiName, externalApiId: s.externalApiId, scannedAt: s.activeFrom, activeTo: s.activeTo })),
        };
        if (!cancelled) {
          setSessionValues(restored);
          saveSessionValues(restored);
        }
      } catch { /* appSettings or endpoint not available, keep localStorage values */ }
    })();
    return () => { cancelled = true; };
  }, [session.id]);

  // Auto-expire session values whose activeTo has passed
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionValues((prev) => {
        const now = new Date();
        const anchorExpired = prev.anchor?.activeTo && new Date(prev.anchor.activeTo) <= now;
        const remainingStickies = prev.stickies.filter(
          (s) => !s.activeTo || new Date(s.activeTo) > now
        );
        const anchorChanged = anchorExpired;
        const stickiesChanged = remainingStickies.length !== prev.stickies.length;

        if (!anchorChanged && !stickiesChanged) return prev;

        const updated = {
          anchor: anchorExpired ? null : prev.anchor,
          stickies: remainingStickies,
        };
        saveSessionValues(updated);
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Keep focus on the input field at all times (only when not in camera mode)
  useEffect(() => {
    if (cameraMode) return;
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };
    focusInput();
    window.addEventListener("click", focusInput);
    return () => window.removeEventListener("click", focusInput);
  }, [cameraMode]);

  const executeScan = useCallback(async (scannedUrl) => {
    const trimmedUrl = scannedUrl.trim();
    if (!trimmedUrl) return;

    setStatus(STATUS_LOADING);
    setLatestResult(null);

    const entry = {
      id: Date.now(),
      url: trimmedUrl,
      timestamp: new Date(),
      status: STATUS_LOADING,
      response: null,
      error: null,
    };

    setScanHistory((prev) => [entry, ...prev].slice(0, 50));

    try {
      // Validate that it looks like a URL
      let url;
      try {
        url = new URL(trimmedUrl);
      } catch {
        throw new Error("Scanned value is not a valid URL");
      }

      // Inject session ID into the URL for session tracking
      const finalUrl = injectSessionId(url.toString(), session.id);

      const response = await fetch(finalUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      let data;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const raw = await response.json();
        // Normalize camelCase keys from ASP.NET Core to PascalCase used throughout this component
        data = {};
        for (const [key, val] of Object.entries(raw)) {
          data[key.charAt(0).toUpperCase() + key.slice(1)] = val;
        }
        // Also normalize nested Data dictionary keys
        if (data.Data && typeof data.Data === "object") {
          const normalized = {};
          for (const [key, val] of Object.entries(data.Data)) {
            normalized[key.charAt(0).toUpperCase() + key.slice(1)] = val;
          }
          data.Data = normalized;
        }
      } else {
        // Some endpoints return 204 No Content on success
        data = response.status === 204
          ? { Success: true, Status: "Completed" }
          : { Success: response.ok, Status: response.statusText };
      }

      const resultEntry = {
        ...entry,
        status: data.Success !== false ? STATUS_SUCCESS : STATUS_ERROR,
        response: data,
        error: data.Success === false ? data.ErrorMessage : null,
      };

      // Track anchor/sticky values from successful enum behavior scans
      if (data.Success !== false && data.Data) {
        const behaviorType = data.Data.BehaviorType;
        const value = data.Data.Value;
        const apiName = data.Data.ApiName;
        const externalApiId = data.Data.ExternalApiId;
        const activeTo = data.Data.ActiveTo || null;
        if (behaviorType && value) {
          setSessionValues((prev) => {
            let updated;
            if (behaviorType === "Anchor") {
              updated = { ...prev, anchor: { value, apiName, externalApiId, scannedAt: new Date().toISOString(), activeTo } };
            } else if (behaviorType === "Sticky") {
              // Replace any existing sticky from the same API, or append if new API
              const filtered = prev.stickies.filter((s) => s.externalApiId !== externalApiId);
              updated = { ...prev, stickies: [...filtered, { value, apiName, externalApiId, scannedAt: new Date().toISOString(), activeTo }] };
            } else {
              updated = prev;
            }
            saveSessionValues(updated);
            return updated;
          });
        }
      }

      setLatestResult(resultEntry);
      setStatus(resultEntry.status);
      setScanHistory((prev) =>
        prev.map((item) => (item.id === entry.id ? resultEntry : item))
      );
    } catch (err) {
      const errorEntry = {
        ...entry,
        status: STATUS_ERROR,
        error: err.message || "Request failed",
      };
      setLatestResult(errorEntry);
      setStatus(STATUS_ERROR);
      setScanHistory((prev) =>
        prev.map((item) => (item.id === entry.id ? errorEntry : item))
      );
    }
  }, [session.id]);

  const handleCameraScan = useCallback((err, result) => {
    if (result) {
      setCameraMode(false);
      executeScan(result.text);
    }
  }, [executeScan]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setScanBuffer(value);

    // Barcode scanners typically send characters rapidly followed by Enter.
    // Reset the timeout on each keystroke so we accumulate the full barcode.
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    // Fallback: if scanner doesn't send Enter, fire after 500ms of inactivity
    scanTimeoutRef.current = setTimeout(() => {
      if (value.trim()) {
        executeScan(value);
        setScanBuffer("");
      }
    }, 500);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      if (scanBuffer.trim()) {
        executeScan(scanBuffer);
        setScanBuffer("");
      }
    }
  };

  const clearHistory = () => {
    setScanHistory([]);
    setLatestResult(null);
    setStatus(STATUS_IDLE);
  };

  const startNewSession = () => {
    const newSession = createNewSession();
    setSession(newSession);
    setSessionValues({ anchor: null, stickies: [] });
    setScanHistory([]);
    setLatestResult(null);
    setStatus(STATUS_IDLE);
  };

  const endSessionValue = useCallback(async (value) => {
    if (!apiBaseUrlRef.current) return;
    try {
      await fetch(
        `${apiBaseUrlRef.current}/ExternalApiMeasurementLogic/EndSessionValue?sessionId=${encodeURIComponent(session.id)}&value=${encodeURIComponent(value)}`,
        { method: "POST" }
      );
    } catch { /* best-effort */ }
  }, [session.id]);

  const removeSticky = (value) => {
    const updated = {
      ...sessionValues,
      stickies: sessionValues.stickies.filter((s) => s.value !== value),
    };
    updateSessionValues(updated);
    endSessionValue(value);
  };

  const clearAnchor = () => {
    const anchorValue = sessionValues.anchor?.value;
    const updated = { ...sessionValues, anchor: null };
    updateSessionValues(updated);
    if (anchorValue) endSessionValue(anchorValue);
  };

  const hasActiveValues = sessionValues.anchor || sessionValues.stickies.length > 0;

  const [valueDetailOpen, setValueDetailOpen] = useState(null); // { type: 'anchor' | 'sticky', data: ... }

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareToast, setShareToast] = useState(false);
  const shareQrRef = useRef(null);

  const currentUrl = window.location.href;

  const handleSharePrint = () => {
    const content = shareQrRef.current;
    const printWindow = window.open("", "_blank", "width=800,height=600");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .print-source { height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; }
            @media print {
              body { margin: 0; padding: 0; }
              .print-source { width: 100%; }
            }
          </style>
        </head>
        <body>
          <div class="print-source">
            <h1 style="text-align: center;">QR Scanner</h1>
            ${content.innerHTML}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleShareCopy = () => {
    navigator.clipboard.writeText(currentUrl);
    setShareToast(true);
  };

  return (
    <div className={classes.root}>
      {/* Top bar */}
      <div className={classes.topBar}>
        <div className={classes.topBarInner}>
          <div className={classes.topBarLeft}>
            <img
              src={process.env.PUBLIC_URL + "/images/Taplr.png"}
              alt="Taplr Logo"
              style={{ height: 36, cursor: "pointer" }}
              onClick={() => window.location.href = "/taplr.app"}
            />
          </div>
          <div className={classes.topBarRight}>
            <button
              type="button"
              onClick={() => setShareDialogOpen(true)}
              className={classes.shareButton} 
              title="Share this page"
            >
              <Share size={18} className={classes.shareButton__icon} aria-hidden="true" focusable="false" />
              <span className={classes.shareButton__label}>Share</span>
            </button>
          </div>
        </div>
      </div>

      {/* Session indicator */}
      <div className={classes.sessionBar}>
        <div className={classes.sessionBarInner}>
          <div className={classes.sessionInfo}>
            <span className={classes.sessionLabel}>Session</span>
            <span className={classes.sessionId}>{session.id.slice(0, 8)}</span>
            <span className={classes.sessionStarted}>
              started {new Date(session.startedAt).toLocaleTimeString()}
            </span>
          </div>
          <Button
            size="small"
            startIcon={<RefreshCw size={14} />}
            onClick={startNewSession}
            sx={{
              color: "#C3996B",
              textTransform: "none",
              fontSize: "0.85rem",
              fontWeight: 600,
              "&:hover": { bgcolor: "rgba(195,153,107,0.08)" },
            }}
          >
            New Session
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className={classes.content}>
        <div className={classes.contentLayout}>
          {/* Active Values Panel - desktop fixed left */}
          {hasActiveValues && (
            <div className={classes.valuesPanel}>
              {sessionValues.anchor && (
                <div className={classes.valueCard}>
                  <div className={classes.valueCardHeader}>
                    <div className={classes.valueTypeRow}>
                      <Anchor size={14} color="#C3996B" />
                      <span className={classes.valueTypeLabel}>Anchor</span>
                    </div>
                    <IconButton onClick={clearAnchor} size="small" sx={{ p: 0.25 }}>
                      <X size={14} color="#888" />
                    </IconButton>
                  </div>
                  {sessionValues.anchor.apiName && (
                    <p className={classes.valueApiName}>{sessionValues.anchor.apiName}</p>
                  )}
                  <p className={classes.valueText}>{sessionValues.anchor.value}</p>
                  <span className={classes.valueTime}>
                    {new Date(sessionValues.anchor.scannedAt).toLocaleTimeString()}
                  </span>
                </div>
              )}

              {sessionValues.stickies.map((sticky) => (
                <div key={sticky.value} className={classes.valueCard}>
                  <div className={classes.valueCardHeader}>
                    <div className={classes.valueTypeRow}>
                      <Tag size={14} color="#6A1B9A" />
                      <span className={classes.valueTypeLabel} style={{ color: "#6A1B9A" }}>Sticky</span>
                    </div>
                    <IconButton onClick={() => removeSticky(sticky.value)} size="small" sx={{ p: 0.25 }}>
                      <X size={14} color="#888" />
                    </IconButton>
                  </div>
                  {sticky.apiName && (
                    <p className={classes.valueApiName}>{sticky.apiName}</p>
                  )}
                  <p className={classes.valueText}>{sticky.value}</p>
                  <span className={classes.valueTime}>
                    {new Date(sticky.scannedAt).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Main content - scanner + results */}
          <div className={classes.container}>

          {/* Active Values - mobile compact chips */}
          {hasActiveValues && (
            <div className={classes.mobileValuesPanel}>
              {sessionValues.anchor && (
                <button
                  type="button"
                  className={classes.mobileValueChip}
                  onClick={() => setValueDetailOpen({ type: "anchor", data: sessionValues.anchor })}
                >
                  <Anchor size={14} color="#C3996B" />
                  <span className={classes.mobileValueChipText}>{sessionValues.anchor.value}</span>
                </button>
              )}
              {sessionValues.stickies.map((sticky) => (
                <button
                  key={sticky.value}
                  type="button"
                  className={classes.mobileValueChip}
                  onClick={() => setValueDetailOpen({ type: "sticky", data: sticky })}
                >
                  <Tag size={14} color="#6A1B9A" />
                  <span className={classes.mobileValueChipText}>{sticky.value}</span>
                </button>
              ))}
            </div>
          )}

          {/* Scanner Input Card */}
          <div className={classes.card}>
            {cameraMode ? (
              <div className={classes.cameraContainer}>
                <BarcodeScannerComponent
                  onUpdate={handleCameraScan}
                  width="100%"
                  height={300}
                  facingMode="environment"
                />
                <div className={classes.cameraModeBar}>
                  <button
                    type="button"
                    className={classes.modeToggleButton}
                    onClick={() => setCameraMode(false)}
                  >
                    <Keyboard size={18} />
                    <span>Switch to keyboard input</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={classes.scannerInputRow}>
                  <StatusIndicator status={status} />
                  <TextField
                    inputRef={inputRef}
                    value={scanBuffer}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Scan a QR code or paste a URL..."
                    variant="outlined"
                    fullWidth
                    autoFocus
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        fontSize: "1.1rem",
                        "& fieldset": { borderColor: "#d3d3d3" },
                        "&:hover fieldset": { borderColor: "#C3996B" },
                        "&.Mui-focused fieldset": { borderColor: "#C3996B" },
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => setCameraMode(true)}
                    title="Switch to camera scanner"
                    sx={{
                      ml: 1,
                      color: "#C3996B",
                      border: "1px solid #d3d3d3",
                      borderRadius: "4px",
                      height: 56,
                      width: 56,
                      "&:hover": { borderColor: "#C3996B", bgcolor: "rgba(195,153,107,0.06)" },
                    }}
                  >
                    <Camera size={22} />
                  </IconButton>
                </div>
                <p className={classes.hint}>
                  Place your cursor here and scan a barcode, or tap the camera button to scan with your device camera.
                </p>
              </>
            )}
          </div>

          {/* Latest Result */}
          {latestResult && (
            <Fade in={true} timeout={300}>
              <div className={classes.card}>
                <ResultDisplay result={latestResult} />
              </div>
            </Fade>
          )}

          {/* History */}
          {scanHistory.length > 1 && (
            <div>
              <p className={classes.sectionLabel}>Previous Scans</p>
              {scanHistory.slice(1).map((entry) => (
                <HistoryEntry key={entry.id} entry={entry} />
              ))}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Value Detail Dialog (mobile) */}
      <Dialog
        open={!!valueDetailOpen}
        onClose={() => setValueDetailOpen(null)}
        PaperProps={{ sx: { width: 340, borderRadius: "8px" } }}
      >
        {valueDetailOpen && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 16px 8px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {valueDetailOpen.type === "anchor" ? (
                  <Anchor size={18} color="#C3996B" />
                ) : (
                  <Tag size={18} color="#6A1B9A" />
                )}
                <span style={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: valueDetailOpen.type === "anchor" ? "#C3996B" : "#6A1B9A",
                }}>
                  {valueDetailOpen.type === "anchor" ? "Anchor" : "Sticky"}
                </span>
              </div>
              <IconButton onClick={() => setValueDetailOpen(null)} size="small" sx={{ p: 0.25 }}>
                <X size={16} color="#888" />
              </IconButton>
            </div>
            <DialogContent sx={{ pt: 0, pb: 2 }}>
              {valueDetailOpen.data.apiName && (
                <p style={{ margin: "0 0 4px 0", fontSize: "0.8rem", color: "#666" }}>
                  {valueDetailOpen.data.apiName}
                </p>
              )}
              <p style={{ margin: "0 0 8px 0", fontSize: "1.05rem", fontWeight: 600, color: "#0C0E29" }}>
                {valueDetailOpen.data.value}
              </p>
              <p style={{ margin: "0 0 16px 0", fontSize: "0.8rem", color: "#888" }}>
                Scanned at {new Date(valueDetailOpen.data.scannedAt).toLocaleTimeString()}
              </p>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<X size={16} />}
                onClick={() => {
                  if (valueDetailOpen.type === "anchor") {
                    clearAnchor();
                  } else {
                    removeSticky(valueDetailOpen.data.value);
                  }
                  setValueDetailOpen(null);
                }}
                sx={{
                  color: "#D84949",
                  borderColor: "#D84949",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "rgba(216,73,73,0.06)", borderColor: "#D84949" },
                }}
              >
                Remove Value
              </Button>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        PaperProps={{ sx: { width: 440, borderRadius: "6px" } }}
        maxWidth="lg"
      >
        <div className={classes.shareHead}>
          <DialogTitle sx={{ fontSize: "24px", fontFamily: "Lato, sans-serif", fontWeight: "normal", lineHeight: "100%", p: 0 }}>
            Share QR Scanner
          </DialogTitle>
          <div className={classes.shareHeadButtons}>
            <button className={classes.shareIconBtn} onClick={handleShareCopy} title="Copy link">
              <ClipboardCopy size={19} />
            </button>
            <button className={classes.shareIconBtn} onClick={handleSharePrint} title="Print QR code">
              <Printer size={19} />
            </button>
            <button className={classes.shareCloseBtn} onClick={() => setShareDialogOpen(false)}>
              <X size={19} />
            </button>
          </div>
        </div>
        <DialogContent sx={{ p: "0 16px", mb: "16px", "&::-webkit-scrollbar": { display: "none" } }}>
          <div className={classes.shareQrContainer}>
            <div ref={shareQrRef} style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
              <QRCode value={currentUrl} bgColor="#ffffff" renderAs="svg" size={200} />
              <div style={{ width: 200, display: "flex", justifyContent: "center", marginTop: 8 }}>
                Powered by Flagstone
              </div>
            </div>
          </div>
          <p className={classes.shareUrlText}>{currentUrl}</p>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={shareToast}
        autoHideDuration={3000}
        onClose={(_, reason) => { if (reason !== "clickaway") setShareToast(false); }}
        message="Link copied to clipboard"
        action={
          <IconButton size="small" color="inherit" onClick={() => setShareToast(false)}>
            <X fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
}

// ---- Sub-components ----

function StatusIndicator({ status }) {
  if (status === STATUS_LOADING) {
    return <CircularProgress size={24} sx={{ color: "#C3996B", mr: 1.5 }} />;
  }
  if (status === STATUS_SUCCESS) {
    return <CheckCircle size={24} color="#2E7D32" style={{ marginRight: 12, flexShrink: 0 }} />;
  }
  if (status === STATUS_ERROR) {
    return <XCircle size={24} color="#D84949" style={{ marginRight: 12, flexShrink: 0 }} />;
  }
  return <QrCode size={24} color="#C3996B" style={{ marginRight: 12, flexShrink: 0 }} />;
}

function ResultDisplay({ result }) {
  const classes = useStyles();
  const isSuccess = result.status === STATUS_SUCCESS;
  const isLoading = result.status === STATUS_LOADING;
  const data = result.response;

  if (isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 0" }}>
        <CircularProgress size={40} sx={{ color: "#C3996B" }} />
        <p style={{ marginTop: 16, color: "#1D3557" }}>Processing scan...</p>
      </div>
    );
  }

  const actionInfo = parseActionFromUrl(result.url);

  return (
    <div>
      {/* Status Banner */}
      <div style={{
        display: "flex",
        alignItems: "center",
        padding: "14px 20px",
        borderRadius: "8px 8px 0 0",
        margin: "-16px -16px 16px -16px",
        background: isSuccess ? "#C3996B" : "#D84949",
      }}>
        {isSuccess ? (
          <CheckCircle size={24} color="#fff" />
        ) : (
          <XCircle size={24} color="#fff" />
        )}
        <div style={{ marginLeft: 12 }}>
          <h2 style={{ color: "#fff", margin: 0, fontSize: "1.2rem", fontWeight: "bold" }}>
            {isSuccess ? "Scan Successful" : "Scan Failed"}
          </h2>
          {data?.Status && (
            <p style={{ color: "rgba(255,255,255,0.85)", margin: 0, fontSize: "0.85rem" }}>
              {data.Status}
            </p>
          )}
        </div>
      </div>

      {/* Action & Details */}
      <div>
        {actionInfo && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <ActionChip action={actionInfo} />
            <span style={{ color: "#888", fontSize: "0.8rem", display: "flex", alignItems: "center" }}>
              <Clock size={14} style={{ marginRight: 4 }} />
              {result.timestamp.toLocaleTimeString()}
            </span>
          </div>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Response Data */}
        {data && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
            {data.Reference && <DataField label="Reference" value={data.Reference} />}
            {data.Action && <DataField label="Action" value={data.Action} />}
            {data.Message && <DataField label="Message" value={data.Message} />}
            {data.Timestamp && <DataField label="Timestamp" value={data.Timestamp} />}
            {data.TimerCount != null && <DataField label="Timers" value={data.TimerCount} />}
            {data.ErrorMessage && <DataField label="Error" value={data.ErrorMessage} isError />}
            {result.error && !data.ErrorMessage && <DataField label="Error" value={result.error} isError />}
            {data.Data && Object.keys(data.Data).length > 0 && (
              <div style={{ gridColumn: "1 / -1" }}>
                <p className={classes.fieldLabel}>Additional Data</p>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: 12,
                  marginTop: 8,
                  padding: 12,
                  background: "#F8F8F8",
                  borderRadius: 8,
                }}>
                  {Object.entries(data.Data).filter(([key]) => key !== "ExternalApiId").map(([key, value]) => (
                    <DataField key={key} label={formatLabel(key)} value={String(value)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!data && result.error && (
          <div style={{ padding: 12, background: "#FDF3F3", borderRadius: 8, border: "1px solid #FBE5E5" }}>
            <p style={{ color: "#D84949", margin: 0 }}>{result.error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ActionChip({ action }) {
  const iconProps = { size: 16, style: { marginRight: 4 } };
  const config = {
    start: { icon: <Play {...iconProps} />, color: "#2E7D32", bg: "#E8F5E9", label: "Start Timer" },
    stop: { icon: <Square {...iconProps} />, color: "#D84949", bg: "#FDF3F3", label: "Stop Timer" },
    linear: { icon: <RotateCcw {...iconProps} />, color: "#C3996B", bg: "#FFF8F0", label: "Linear Stopwatch" },
    count: { icon: <Hash {...iconProps} />, color: "#E65100", bg: "#FFF3E0", label: "Count" },
    enum: { icon: <QrCode {...iconProps} />, color: "#6A1B9A", bg: "#F3E5F5", label: "Option Selection" },
    measurement: { icon: <QrCode {...iconProps} />, color: "#1D3557", bg: "#EDEDED", label: "Measurement" },
  };

  const cfg = config[action.type] || config.measurement;

  return (
    <Chip
      icon={cfg.icon}
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        borderRadius: "4px",
        "& .MuiChip-icon": { color: cfg.color },
      }}
    />
  );
}

function DataField({ label, value, isError }) {
  const classes = useStyles();
  return (
    <div>
      <p className={classes.fieldLabel}>{label}</p>
      <p className={classes.fieldValue} style={{
        color: isError ? "#D84949" : "#0C0E29",
      }}>
        {value}
      </p>
    </div>
  );
}

function HistoryEntry({ entry }) {
  const classes = useStyles();
  const isSuccess = entry.status === STATUS_SUCCESS;
  const isLoading = entry.status === STATUS_LOADING;
  const actionInfo = parseActionFromUrl(entry.url);

  return (
    <div className={classes.historyEntry}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isLoading ? (
            <CircularProgress size={16} sx={{ color: "#C3996B", mr: 0.5 }} />
          ) : isSuccess ? (
            <CheckCircle size={16} color="#C3996B" style={{ marginRight: 4 }} />
          ) : (
            <XCircle size={16} color="#D84949" style={{ marginRight: 4 }} />
          )}
          {actionInfo && <ActionChip action={actionInfo} />}
        </div>
        <span style={{ color: "#888", fontSize: "0.8rem" }}>
          {entry.timestamp.toLocaleTimeString()}
        </span>
      </div>
      {entry.response?.Message && (
        <p style={{ marginTop: 4, marginBottom: 0, color: "#1D3557", fontSize: "0.9rem", marginLeft: 28 }}>
          {entry.response.Message}
        </p>
      )}
      {entry.error && (
        <p style={{ marginTop: 4, marginBottom: 0, color: "#D84949", fontSize: "0.9rem", marginLeft: 28 }}>
          {entry.error}
        </p>
      )}
    </div>
  );
}

// ---- Helpers ----

function parseActionFromUrl(url) {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname.toLowerCase();

    if (path.includes("linearstopwatch")) return { type: "linear" };
    if (path.includes("start")) return { type: "start" };
    if (path.includes("stop")) return { type: "stop" };
    if (path.includes("enumbehavior")) return { type: "enum" };
    if (path.includes("query") || path.includes("count")) return { type: "count" };
    if (path.includes("measurement")) return { type: "measurement" };
    if (path.includes("measure")) return { type: "measurement" };
    return { type: "measurement" };
  } catch {
    return null;
  }
}

function formatLabel(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// ---- Styles ----

const useStyles = makeStyles({
  root: {
    position: "absolute",
    top: 0,
    width: "100%",
  },
  topBar: {
    width: "100%",
    background: "#C3996B",
    display: "flex",
    justifyContent: "center",
  },
  topBarInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "90%",
    padding: "12px 0",
  },
  topBarLeft: {
    width: "33%",
    display: "flex",
    alignItems: "center",
  },
  topBarRight: {
    width: "33%",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  shareButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "transparent",
    height: "40px",
    padding: "6px 12px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
  },
  shareButton__icon: {
    color: "white",
    opacity: 0.8,
  },
  shareButton__label: {
    color: "white",
    fontSize: "1rem",
  },
  sessionBar: {
    width: "100%",
    background: "#FFFFFF",
    borderBottom: "1px solid #EDEDED",
    display: "flex",
    justifyContent: "center",
  },
  sessionBarInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "90%",
    maxWidth: 960,
    padding: "8px 0",
    marginLeft: "auto",
    marginRight: "auto",
  },
  sessionInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sessionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  sessionId: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#0C0E29",
    fontFamily: "monospace",
  },
  sessionStarted: {
    fontSize: "0.8rem",
    color: "#888",
  },
  content: {
    padding: "24px 0",
  },
  contentLayout: {
    display: "flex",
    maxWidth: 960,
    width: "90%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  valuesPanel: {
    position: "fixed",
    top: 134,
    left: 16,
    width: 200,
    zIndex: 100,
    maxHeight: "calc(100vh - 154px)",
    overflowY: "auto",
    "@media (max-width: 768px)": {
      display: "none",
    },
  },
  valuesPanelTitle: {
    color: "#0C0E29",
    fontWeight: "normal",
    fontSize: "1rem",
    margin: "0 0 12px 0",
  },
  valueCard: {
    background: "#FFFFFF",
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 8,
  },
  valueCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  valueTypeRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  valueTypeLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#C3996B",
  },
  valueText: {
    margin: "4px 0 2px 0",
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#0C0E29",
  },
  valueApiName: {
    margin: "4px 0 0 0",
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "#666",
  },
  valueTime: {
    fontSize: "0.75rem",
    color: "#888",
  },
  mobileValuesPanel: {
    display: "none",
    flexWrap: "wrap",
    gap: 8,
    "@media (max-width: 768px)": {
      display: "flex",
    },
  },
  mobileValueChip: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    background: "#FFFFFF",
    border: "1px solid #EDEDED",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#0C0E29",
    maxWidth: "100%",
    transition: "all 0.15s",
    "&:hover": {
      borderColor: "#C3996B",
      background: "rgba(195,153,107,0.04)",
    },
  },
  mobileValueChipText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 120,
  },
  container: {
    boxSizing: "border-box",
    maxWidth: "720px",
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    margin: "0 auto",
  },
  card: {
    background: "#FFFFFF",
    borderRadius: "8px",
    padding: "16px",
    boxSizing: "border-box",
  },
  scannerInputRow: {
    display: "flex",
    alignItems: "center",
  },
  cameraContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    "& video": {
      borderRadius: 8,
      width: "100%",
      maxHeight: 300,
      objectFit: "cover",
    },
  },
  cameraModeBar: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  modeToggleButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "none",
    border: "1px solid #d3d3d3",
    borderRadius: 4,
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: "0.9rem",
    color: "#C3996B",
    fontWeight: 600,
    transition: "all 0.2s",
    "&:hover": {
      borderColor: "#C3996B",
      backgroundColor: "rgba(195,153,107,0.06)",
    },
  },
  hint: {
    marginTop: 8,
    marginBottom: 0,
    color: "#888",
    fontSize: "0.85rem",
  },
  sectionLabel: {
    color: "#1D3557",
    fontWeight: 600,
    fontSize: "0.85rem",
    letterSpacing: 0.3,
    marginBottom: 12,
    marginTop: 8,
  },
  fieldLabel: {
    color: "#888",
    fontWeight: 600,
    textTransform: "uppercase",
    fontSize: "0.7rem",
    letterSpacing: 0.5,
    margin: 0,
  },
  fieldValue: {
    fontWeight: 500,
    fontSize: "0.95rem",
    marginTop: 2,
    marginBottom: 0,
    wordBreak: "break-word",
    color: "#0C0E29",
  },
  historyEntry: {
    background: "#FFFFFF",
    borderRadius: "8px",
    padding: "12px 16px",
    boxSizing: "border-box",
    marginBottom: 8,
  },
  shareHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 16px 0 16px",
    marginBottom: 8,
  },
  shareHeadButtons: {
    display: "flex",
    gap: 8,
  },
  shareIconBtn: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    padding: 3,
    height: 25,
    borderRadius: 3,
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#E0E0E0",
    },
  },
  shareCloseBtn: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    padding: 3,
    height: 25,
    borderRadius: 3,
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#E0E0E0",
    },
  },
  shareQrContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #EDEDED",
    borderRadius: 8,
    padding: "24px 16px",
  },
  shareUrlText: {
    marginTop: 12,
    fontSize: 13,
    color: "#666",
    textAlign: "center",
    wordBreak: "break-all",
  },
});

export default QrScannerPage;
